import React, {useRef} from 'react';
import {GAME_PANEL_MATERIALS, GAME_PANEL_SHAPES, GamePanel} from './GamePanel';
import {joinClassNames} from './joinClassNames';
import './BottomSheet.css';

// Дальше скольких пикселей отпущенный лист закрывается
export const BOTTOM_SHEET_CLOSE_DISTANCE_PX = 80;
// Рывок вниз закрывает и на коротком пути: скорость, px/мс
export const BOTTOM_SHEET_CLOSE_VELOCITY = 0.5;
// Короче этого рывок за свайп не считается — случайное касание
const MIN_FLICK_DISTANCE_PX = 16;

/**
 * Сдвиг листа пальцем — прямо в DOM (style.transform), без состояния React:
 * перерисовка листа с содержимым (рынок, статы игрока) и пересчет стилей
 * всего его дерева на каждое движение пальца давали на телефоне низкий FPS.
 * Пока тянут — класс bottom-sheet_dragging: без перехода и на своем слое
 * композиции. Отпустили — сдвиг снят, и лист доезжает переходом из CSS
 */
const setSheetDragOffset = (sheet, offset) => {
  if (!sheet) {
    return;
  }
  sheet.classList.toggle('bottom-sheet_dragging', offset > 0);
  sheet.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
};

/**
 * Лист снизу (bottom sheet) для телефона: плита базальта во всю ширину экрана,
 * прижата к низу, высота по содержимому (не выше места под шапкой). Тянется
 * вниз за ручку и шапку листа (header): дальше BOTTOM_SHEET_CLOSE_DISTANCE_PX
 * или рывком — закрывается, иначе возвращается. Тело (children) прокручивается
 * как обычно — свайп начинается только в верхней зоне. Под листом прозрачная
 * подложка: нажатие мимо закрывает лист и не доходит до поля. Закрытый лист
 * уезжает вниз и остается смонтированным.
 *
 * @param {boolean} isOpen
 * @param {function(): void} onClose
 * @param {React.ReactNode} [header] - шапка листа: тянется вместе с ручкой
 * @param {string} [className] - на обертке листа
 */
export function BottomSheet({isOpen, onClose, header = null, className, children, ...rest}) {
  const dragRef = useRef(null);

  const handlePointerDown = (event) => {
    // Кнопки в шапке листа (крестик) нажимаются, а не тянут лист
    if (!isOpen || event.target.closest('button')) {
      return;
    }
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTime: Date.now(),
      sheet: event.currentTarget.closest('.bottom-sheet'),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    // Вверх лист не тянется: он и так по содержимому
    setSheetDragOffset(drag.sheet, Math.max(0, event.clientY - drag.startY));
  };

  const handlePointerEnd = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    const distance = Math.max(0, event.clientY - drag.startY);
    const velocity = distance / Math.max(1, Date.now() - drag.startTime);
    setSheetDragOffset(drag.sheet, 0);
    if (event.type === 'pointercancel') {
      return;
    }
    if (distance >= BOTTOM_SHEET_CLOSE_DISTANCE_PX
      || (distance >= MIN_FLICK_DISTANCE_PX && velocity >= BOTTOM_SHEET_CLOSE_VELOCITY)) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <div className={'bottom-sheet_backdrop'} onClick={onClose} aria-hidden={true}/>
      )}

      <GamePanel
        material={GAME_PANEL_MATERIALS.SHEET}
        shape={GAME_PANEL_SHAPES.TOP}
        shadow
        className={joinClassNames(
          'bottom-sheet',
          isOpen && 'bottom-sheet_open',
          className,
        )}
        aria-hidden={!isOpen}
        {...rest}
      >
        <div
          className={'bottom-sheet_drag-zone'}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
        >
          <span className={'bottom-sheet_handle'} aria-hidden={true}/>
          {header}
        </div>

        {children}
      </GamePanel>
    </>
  );
}

export default BottomSheet;
